
import React, { useState, useMemo, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

export interface BusinessImageProps {
  imageUrls: string[] | undefined;
  fallbackUrl: string | undefined;
  placeholderSrc: string;
  altText: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
}

const BusinessImage: React.FC<BusinessImageProps> = ({
  imageUrls,
  fallbackUrl,
  placeholderSrc,
  altText,
  className = "",
  onLoad,
  onError
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();
  
  // Helper function to validate URLs
  const isValidImageUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch (e) {
      console.log(`Invalid URL format: ${url}`);
      return false;
    }
  };
  
  // Reset error state when imageUrls or fallbackUrl changes
  useEffect(() => {
    setHasError(false);
    setCurrentImageIndex(0);
    setIsLoading(true);
  }, [imageUrls, fallbackUrl]);
  
  // Filter valid image URLs
  const validImageUrls = useMemo(() => {
    if (!imageUrls) return [];
    return imageUrls.filter(url => isValidImageUrl(url));
  }, [imageUrls]);
  
  // Get the best available image using a priority order
  const displayImage = useMemo(() => {
    // Try array of images first
    if (!hasError && validImageUrls.length > currentImageIndex) {
      return validImageUrls[currentImageIndex];
    }
    
    // Then try fallback URL if valid
    if (!hasError && isValidImageUrl(fallbackUrl)) {
      return fallbackUrl;
    }
    
    // Finally use placeholder - always ensure we have a valid image
    return placeholderSrc;
  }, [validImageUrls, fallbackUrl, placeholderSrc, hasError, currentImageIndex]);
  
  const handleImageError = () => {
    console.log(`Image failed to load for ${altText}:`, displayImage);
    
    // Try next image in array if available
    if (validImageUrls && validImageUrls.length > currentImageIndex + 1) {
      console.log(`Trying next image for ${altText}`);
      setCurrentImageIndex(currentImageIndex + 1);
      return;
    }
    
    // If we've tried all images, try the fallback URL
    if (!hasError && isValidImageUrl(fallbackUrl) && displayImage !== fallbackUrl) {
      console.log(`Trying fallback URL for ${altText}`);
      setHasError(true);
      return;
    }
    
    // If fallback URL failed or doesn't exist, use placeholder
    console.log(`Using placeholder for ${altText}`);
    setHasError(true);
    setIsLoading(false);
    if (onError) onError();
  };
  
  const handleImageLoad = () => {
    console.log(`Image loaded successfully for ${altText}:`, displayImage);
    setIsLoading(false);
    if (onLoad) onLoad();
  };
  
  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-eco-500"></div>
        </div>
      )}
      <img
        src={displayImage}
        alt={altText}
        className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300 w-full h-full object-cover`}
        onError={handleImageError}
        onLoad={handleImageLoad}
        crossOrigin="anonymous"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </div>
  );
};

export default BusinessImage;
